import { FormEvent, ReactNode, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTheme } from "@hooks";
import { LoginContent } from "@templates";
import { MapiqLogo } from "@atoms";
import { Button, Input } from "@molecules";
import { FlexCol, P } from "@quarks";
import { StyledCard, StyledH1 } from "./styles";

const scopes: string[] = (import.meta.env.MAPIQ_AUTH_LOGIN_SCOPES ?? "")
  .split(",")
  .map((scope: string) => scope.trim());

// Same B2C tenant as the regular sign in, but with the (experimental) magic link policy
const magicLinkAuthority = (import.meta.env.MAPIQ_AUTH_AUTHORITY ?? "").replace(/[^/]+$/, "B2C_1A_MagicLink");

// The token is passed in the fragment, so it's never sent to a server and doesn't end up in any request logs
const readTokenHint = () => new URLSearchParams(window.location.hash.slice(1)).get("token");

// Proof of concept: hardcoded to the identity service of the test environment, which logs the link instead of mailing it
const identityServiceUrl = "https://test-idty-app-c7a2f.azurewebsites.net";

// The identity service only sends the link if the email address is eligible to sign in, but always responds the same
const requestMagicLink = async (email: string) => {
  const response = await fetch(new URL("/api/magic-link", identityServiceUrl), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (response.status === 429) throw new Error("Too many requests, please try again in a minute.");
  if (!response.ok) throw new Error("Something went wrong, please check your email address and try again.");
};

const MagicLinkCard = ({ title, message, children }: { title: string; message: string; children?: ReactNode }) => {
  const { theme } = useTheme();

  return (
    <LoginContent>
      <StyledCard>
        <MapiqLogo
          width="123px"
          theme={theme === "light" ? "dark" : "light"}
          type="full"
        />
        <FlexCol
          alignItems="center"
          rowGap={8}
          sm={{ rowGap: "16px" }}>
          <StyledH1>{title}</StyledH1>
          <P>{message}</P>
        </FlexCol>
        {children}
      </StyledCard>
    </LoginContent>
  );
};

const MagicLinkRequest = () => {
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSending(true);
    setError(undefined);
    try {
      await requestMagicLink(email.trim());
      setIsSent(true);
    } catch (requestError) {
      console.error(requestError);
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setIsSending(false);
    }
  };

  if (isSent) {
    // Same message whether the account exists or not, so this page can't be used to find out who has an account
    return (
      <MagicLinkCard
        title="Check your inbox"
        message={`If ${email.trim()} can sign in to Mapiq, you'll receive an email with a sign in link.`}
      />
    );
  }

  return (
    <MagicLinkCard
      title="Sign in"
      message="Enter your email address and we'll send you a link to sign in.">
      <form onSubmit={submit}>
        <FlexCol rowGap={16}>
          <Input
            type="text"
            inputMode="email"
            autoComplete="email"
            placeholder="Email address"
            size="full"
            value={email}
            onChange={event => setEmail(event.target.value)}
          />
          {error && <P>{error}</P>}
          <Button
            type="submit"
            button="primary"
            size="full"
            loading={isSending}
            disabled={!email.trim()}>
            Send me a sign in link
          </Button>
        </FlexCol>
      </form>
    </MagicLinkCard>
  );
};

const MagicLinkContinue = ({ tokenHint }: { tokenHint: string }) => {
  const { instance } = useMsal();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const signIn = async () => {
    setIsSigningIn(true);
    try {
      // Settle any pending redirect first, as MSAL only allows one interaction at a time
      await instance.handleRedirectPromise();
      await instance.loginRedirect({
        scopes,
        authority: magicLinkAuthority,
        redirectUri: window.location.origin,
        // Complete the sign in on the root instead of navigating back to this page, which has no token anymore
        redirectStartPage: `${window.location.origin}/`,
        extraQueryParameters: { id_token_hint: tokenHint },
      });
    } catch (error) {
      console.error(error);
      setIsSigningIn(false);
    }
  };

  // The sign in only starts on a click, so link scanners in email clients that open the link don't use it up
  return (
    <MagicLinkCard
      title="Welcome back"
      message="Continue to sign in to Mapiq.">
      <Button
        button="primary"
        size="full"
        loading={isSigningIn}
        onClick={signIn}>
        Continue
      </Button>
    </MagicLinkCard>
  );
};

/**
 * Magic link (proof of concept): without a token it lets the user request a sign in link by email, with the token from
 * that link it signs in the user with it as id_token_hint. B2C redirects back to the origin, where the regular redirect
 * handling (useLoginRedirectResult) completes the sign in.
 */
export const MagicLinkSignIn = () => {
  const [tokenHint] = useState(readTokenHint);

  useEffect(() => {
    // Don't keep the token in the address bar or browser history
    if (tokenHint) window.history.replaceState(null, "", window.location.pathname);
  }, [tokenHint]);

  return tokenHint ? <MagicLinkContinue tokenHint={tokenHint} /> : <MagicLinkRequest />;
};
